import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DateInput from "@/components/DateInput";
import { toast } from "sonner";
import {
  Package, CheckCircle, WarningCircle, XCircle, ArrowLeft, ArrowClockwise,
  Truck, Printer, Barcode, ClipboardText, CaretRight, ArrowCounterClockwise,
  Plus, Trash, Receipt,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import ProveedorCombobox from "@/components/ProveedorCombobox";
import ProductoCombobox from "@/components/ProductoCombobox";
import { useZebraPrinter, generarZplEtiqueta } from "@/components/ZebraPrinter";
import { useAuth } from "@/context/AuthContext";
import NumberInput from "@/components/ui/NumberInput";

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (s) => {
  if (!s) return "—";
  try { const [y, m, d] = s.split("-"); return `${d}/${m}/${y.slice(2)}`; } catch { return s; }
};

const CONDICION_STYLES = {
  ok: "bg-emerald-500 hover:bg-emerald-600 text-white",
  observada: "bg-amber-500 hover:bg-amber-600 text-white",
  rechazada: "bg-rose-500 hover:bg-rose-600 text-white",
};

const emptyItem = () => ({
  descripcion: "",
  codigo: "",
  producto_id: null,
  peso_real: "",
  cantidad_bultos: "",
  condicion: "ok",
  observaciones: "",
});

export default function IngresoMercaderiaTab() {
  const [ingresos, setIngresos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fechaDesde] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [fechaHasta] = useState(todayISO());
  const [showForm, setShowForm] = useState(false);
  const [selIngreso, setSelIngreso] = useState(null);
  const [lotesModal, setLotesModal] = useState(null);
  const [soloSinCompra, setSoloSinCompra] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ing, prov] = await Promise.all([
        api.get("/ingresos-mercaderia", { params: { fecha_desde: fechaDesde, fecha_hasta: fechaHasta } }),
        api.get("/proveedores"),
      ]);
      setIngresos(Array.isArray(ing.data) ? ing.data : []);
      setProveedores(Array.isArray(prov.data) ? prov.data.filter((p) => p.tipo_proveedor !== "servicios_insumos") : []);
    } catch { toast.error("Error al cargar ingresos"); }
    finally { setLoading(false); }
  }, [fechaDesde, fechaHasta]);
  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const total = ingresos.length;
    const pendientes = ingresos.filter((i) => i.estado === "pendiente_compra").length;
    const conCompra = ingresos.filter((i) => i.estado === "con_compra").length;
    const revertidas = ingresos.filter((i) => i.estado === "revertida").length;
    return { total, pendientes, conCompra, revertidas };
  }, [ingresos]);

  const ingresosFiltrados = useMemo(
    () => (soloSinCompra ? ingresos.filter((i) => i.estado === "pendiente_compra") : ingresos),
    [ingresos, soloSinCompra],
  );

  if (showForm) {
    return (
      <IngresoForm
        proveedores={proveedores}
        onCancel={() => setShowForm(false)}
        onSaved={(res) => {
          setShowForm(false);
          setLotesModal({ lotes: res.lotes_generados || [], proveedor: res.proveedor_nombre, reprint: false });
          load();
        }}
      />
    );
  }

  if (selIngreso && !lotesModal) {
    return (
      <IngresoDetalle
        ingreso={selIngreso}
        onBack={() => { setSelIngreso(null); load(); }}
        onReimprimir={async () => {
          try {
            const { data } = await api.get(`/ingresos-mercaderia/${selIngreso.id}/lotes`);
            if (!data?.length) { toast.info("No se encontraron lotes"); return; }
            setLotesModal({ lotes: data, proveedor: selIngreso.proveedor_nombre, reprint: true });
          } catch { toast.error("No se pudieron cargar los lotes"); }
        }}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <label
          className="inline-flex items-center gap-2 text-[12px] font-medium cursor-pointer select-none px-3 py-1.5 border border-neutral-300 rounded-md bg-white hover:bg-neutral-50 transition-colors"
          data-testid="filtro-solo-sin-compra"
        >
          <input
            type="checkbox"
            className="accent-[#921A0E]"
            checked={soloSinCompra}
            onChange={(e) => setSoloSinCompra(e.target.checked)}
          />
          <WarningCircle size={14} weight="duotone" className={soloSinCompra ? "text-amber-600" : "text-neutral-400"} />
          <span>Sólo pendientes de compra</span>
          <Badge variant="outline" className="text-[10px] ml-1 font-mono">
            {stats.pendientes}
          </Badge>
        </label>
        <div className="flex-1" />
        <Button
          onClick={() => setShowForm(true)}
          className="bg-[#921A0E] hover:bg-[#7d1611] text-white gap-2"
          data-testid="btn-nuevo-ingreso"
        >
          <Plus size={16} weight="bold" /> Nuevo ingreso
        </Button>
      </div>

      {loading && <div className="text-neutral-500 text-sm py-6">Cargando…</div>}
      {!loading && ingresosFiltrados.length === 0 && (
        <div className="border border-dashed border-neutral-300 bg-neutral-50 rounded-lg p-10 text-center">
          <Package size={40} weight="thin" className="mx-auto mb-3 text-neutral-400" />
          <div className="text-neutral-700 font-medium">
            {soloSinCompra && ingresos.length > 0
              ? "No hay ingresos pendientes de compra"
              : "No hay ingresos en el rango seleccionado"}
          </div>
          {!soloSinCompra && (
            <div className="text-xs text-neutral-500 mt-1">Presioná "Nuevo ingreso" para cargar mercadería sin precios.</div>
          )}
        </div>
      )}

      <div className="space-y-3" data-testid="ingresos-lista">
        {ingresosFiltrados.map((i) => (
          <IngresoRow key={i.id} ingreso={i} onOpen={() => setSelIngreso(i)} onReload={load} />
        ))}
      </div>

      {lotesModal && (
        <LotesGeneradosDialog
          open={!!lotesModal}
          lotes={lotesModal.lotes}
          proveedor={lotesModal.proveedor}
          onClose={() => {
            if (lotesModal.reprint) setLotesModal(null);
            else { setLotesModal(null); setSelIngreso(null); load(); }
          }}
        />
      )}
    </div>
  );
}

function IngresoRow({ ingreso, onOpen, onReload }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const puedeCargarCompra = user?.is_admin || (user?.permissions || []).includes("compras");

  const cargarCompra = (e) => {
    e.stopPropagation();
    navigate(`/compras?ingreso_id=${ingreso.id}`);
  };

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left border border-neutral-300 rounded-lg bg-white hover:border-[#921A0E] hover:shadow-md transition-all overflow-hidden group"
      data-testid={`ingreso-row-${ingreso.id}`}
    >
      {/* Encabezado */}
      <div className="flex items-center gap-4 p-4">
        <div className={`w-14 h-14 rounded-lg flex items-center justify-center shrink-0 ${
          ingreso.estado === "con_compra" ? "bg-emerald-100 text-emerald-700"
          : ingreso.estado === "revertida" ? "bg-neutral-100 text-neutral-500"
          : "bg-amber-100 text-amber-700"
        }`}>
          {ingreso.estado === "con_compra" ? <CheckCircle size={30} weight="fill" />
            : ingreso.estado === "revertida" ? <ArrowCounterClockwise size={28} weight="duotone" />
            : <Package size={28} weight="duotone" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-lg font-semibold truncate">{ingreso.proveedor_nombre || "—"}</div>
          <div className="text-[11px] text-neutral-500 font-mono mt-0.5 flex items-center gap-2 flex-wrap">
            <span>📅 {fmtDate(ingreso.fecha)}</span>
            <span className="text-neutral-300">·</span>
            <span>{(ingreso.items || []).length} ítem{(ingreso.items || []).length === 1 ? "" : "s"}</span>
            {ingreso.total_kg_real > 0 && (
              <>
                <span className="text-neutral-300">·</span>
                <span className="font-semibold text-neutral-700">{ingreso.total_kg_real.toLocaleString("es-AR")} kg</span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <IngresoBadge estado={ingreso.estado} />
          {ingreso.estado === "con_compra" && ingreso.compra_numero && (
            <div className="text-[10px] font-mono text-emerald-700 flex items-center gap-1" data-testid={`ingreso-compra-num-${ingreso.id}`}>
              <Receipt size={11} weight="bold" />
              Fact. #{ingreso.compra_numero}
            </div>
          )}
        </div>
        {ingreso.estado === "pendiente_compra" && puedeCargarCompra && (
          <span
            role="button"
            tabIndex={0}
            onClick={cargarCompra}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); cargarCompra(e); } }}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 cursor-pointer shrink-0"
            data-testid={`btn-cargar-compra-${ingreso.id}`}
          >
            <Receipt size={14} weight="bold" /> Cargar compra
          </span>
        )}
        <CaretRight size={20} className="text-neutral-400 group-hover:text-[#921A0E] transition-colors shrink-0" />
      </div>

      {/* Preview de productos cargados */}
      {(ingreso.items || []).length > 0 && (
        <div className="border-t border-neutral-100 bg-neutral-50/60 px-4 py-2.5" data-testid={`ingreso-items-preview-${ingreso.id}`}>
          <div className="text-[9px] uppercase tracking-widest font-mono text-neutral-400 mb-1.5">
            Productos cargados
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(ingreso.items || []).slice(0, 6).map((it, idx) => {
              const kg = parseFloat(it.peso_real || it.kilos || 0) || 0;
              return (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 rounded-full pl-2 pr-2.5 py-0.5 text-[11px] font-mono"
                >
                  <span className="text-neutral-700 truncate max-w-[14rem]">{it.descripcion || "—"}</span>
                  {kg > 0 && (
                    <span className="font-semibold text-[#921A0E] bg-[#921A0E]/5 px-1.5 rounded-sm text-[10px]">
                      {kg.toLocaleString("es-AR")} kg
                    </span>
                  )}
                </span>
              );
            })}
            {(ingreso.items || []).length > 6 && (
              <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-mono text-neutral-500">
                +{ingreso.items.length - 6} más
              </span>
            )}
          </div>
        </div>
      )}
    </button>
  );
}

function IngresoForm({ proveedores, onCancel, onSaved }) {
  const [fecha, setFecha] = useState(todayISO());
  const [proveedorId, setProveedorId] = useState("");
  const [obs, setObs] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);

  const totalKg = useMemo(
    () => items.reduce((a, it) => a + (parseFloat(it.peso_real) || 0), 0),
    [items]
  );

  const setItem = (idx, patch) => setItems((s) => s.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((s) => [...s, emptyItem()]);
  const rmItem = (idx) => setItems((s) => s.filter((_, i) => i !== idx));

  const onSelectProducto = (idx, p) => {
    setItem(idx, {
      descripcion: p.nombre || "",
      codigo: p.codigo || "",
      producto_id: p.id || null,
    });
  };

  const submit = async () => {
    if (!proveedorId) return toast.error("Elegí un proveedor");
    const validItems = items.filter((it) => (it.descripcion || "").trim() && ((parseFloat(it.peso_real) || 0) > 0 || it.condicion === "rechazada"));
    if (!validItems.length) return toast.error("Cargá al menos un ítem con peso > 0");
    // Regla dura: cada ítem tiene que estar vinculado al catálogo. Sin esto,
    // el stock se llena de productos huérfanos ("BIFE STOCK TEST").
    const sinCatalogo = validItems.find((it) => !it.producto_id);
    if (sinCatalogo) {
      return toast.error(
        `El ítem "${sinCatalogo.descripcion}" no está vinculado al catálogo. Elegilo de Productos y Precios (o creá uno nuevo desde el combobox).`,
      );
    }
    setSaving(true);
    try {
      const payload = {
        proveedor_id: proveedorId,
        fecha,
        observaciones: obs || null,
        items: validItems.map((it) => ({
          descripcion: it.descripcion.trim(),
          codigo: it.codigo || null,
          producto_id: it.producto_id || null,
          peso_real: parseFloat(it.peso_real) || 0,
          cantidad_bultos: it.cantidad_bultos ? parseInt(it.cantidad_bultos) : null,
          condicion: it.condicion,
          observaciones: it.observaciones || null,
        })),
      };
      const { data } = await api.post("/ingresos-mercaderia", payload);
      toast.success(`Ingreso registrado · ${(data.lotes_generados || []).length} lote(s) generado(s)`);
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar el ingreso");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Button variant="outline" size="sm" onClick={onCancel} className="gap-1">
          <ArrowLeft size={16} /> Volver
        </Button>
        <div className="flex-1">
          <h2 className="text-xl font-bold font-heading">Nuevo ingreso de mercadería</h2>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 mb-4">
        <div>
          <label className="text-xs uppercase font-mono text-neutral-500">Proveedor</label>
          <ProveedorCombobox
            proveedores={proveedores}
            value={proveedorId}
            onChange={setProveedorId}
            testId="ingreso-form-proveedor"
          />
        </div>
      </div>

      <div className="border border-neutral-300 rounded-lg bg-white overflow-hidden mb-4">
        <div className="px-4 py-2 bg-neutral-50 border-b border-neutral-200 grid grid-cols-12 gap-3 text-xs font-mono uppercase text-neutral-600">
          <div className="col-span-4">Producto</div>
          <div className="col-span-2 text-right">Peso real (kg)</div>
          <div className="col-span-1 text-center">Bultos</div>
          <div className="col-span-3">Condición</div>
          <div className="col-span-2 text-right">Acciones</div>
        </div>
        {items.map((it, idx) => (
          <div key={idx} className="border-t border-neutral-200 px-4 py-3">
            <div className="grid grid-cols-12 gap-3 items-center">
              <div className="col-span-4 min-w-0">
                <ProductoCombobox
                  kind="productos"
                  value={{ codigo: it.codigo, nombre: it.descripcion }}
                  onSelect={(p) => onSelectProducto(idx, p)}
                  placeholder="Buscar producto o crear nuevo…"
                  testid={`ingreso-item-producto-${idx}`}
                />
                {!it.producto_id && it.descripcion && (
                  <div className="text-[11px] text-red-700 mt-1 font-mono font-bold">
                    ⛔ Este ítem NO está vinculado al catálogo. Elegilo de Productos y Precios (o creá uno nuevo desde el combobox) antes de guardar.
                  </div>
                )}
              </div>
              <div className="col-span-2">
                <NumberInput
                  step="0.01"
                  value={it.peso_real}
                  onChange={(e) => setItem(idx, { peso_real: e.target.value })}
                  className="text-right text-xl font-bold h-12 font-mono"
                  placeholder="0.00"
                  data-testid={`ingreso-item-peso-${idx}`}
                />
              </div>
              <div className="col-span-1">
                <NumberInput
                  min="0"
                  step="1"
                  value={it.cantidad_bultos}
                  onChange={(e) => setItem(idx, { cantidad_bultos: e.target.value })}
                  className="text-center text-lg font-bold h-12 font-mono"
                  placeholder="—"
                  data-testid={`ingreso-item-bultos-${idx}`}
                />
              </div>
              <div className="col-span-3 flex gap-1">
                {["ok", "observada", "rechazada"].map((cond) => (
                  <Button
                    key={cond}
                    type="button"
                    size="sm"
                    onClick={() => setItem(idx, { condicion: cond })}
                    className={`h-10 px-2 text-xs font-bold flex-1 ${it.condicion === cond ? CONDICION_STYLES[cond] : "bg-neutral-100 hover:bg-neutral-200 text-neutral-700"}`}
                    data-testid={`ingreso-item-cond-${idx}-${cond}`}
                  >
                    {cond === "ok" ? <CheckCircle size={14} weight="bold" />
                     : cond === "observada" ? <WarningCircle size={14} weight="bold" />
                     : <XCircle size={14} weight="bold" />}
                    <span className="ml-1 hidden lg:inline">{cond.toUpperCase()}</span>
                  </Button>
                ))}
              </div>
              <div className="col-span-2 flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => rmItem(idx)}
                  className="text-rose-600 hover:bg-rose-50"
                  disabled={items.length === 1}
                  data-testid={`ingreso-item-remove-${idx}`}
                >
                  <Trash size={14} /> Quitar
                </Button>
              </div>
            </div>
            {(it.condicion === "observada" || it.condicion === "rechazada") && (
              <Textarea
                className="mt-2 text-sm"
                placeholder="Observaciones del ítem (opcional)"
                value={it.observaciones}
                onChange={(e) => setItem(idx, { observaciones: e.target.value })}
                rows={2}
                data-testid={`ingreso-item-obs-${idx}`}
              />
            )}
          </div>
        ))}
        <div className="p-3 border-t border-neutral-200 flex justify-between items-center">
          <Button variant="outline" size="sm" onClick={addItem} className="gap-1" data-testid="ingreso-item-add">
            <Plus size={14} /> Agregar ítem
          </Button>
          <div className="text-sm font-mono">
            Total: <span className="font-bold">{totalKg.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg</span>
          </div>
        </div>
      </div>

      <Textarea
        placeholder="Observaciones generales del ingreso (opcional)"
        value={obs}
        onChange={(e) => setObs(e.target.value)}
        rows={2}
        className="mb-4"
        data-testid="ingreso-form-obs"
      />

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="lg" onClick={onCancel}>Cancelar</Button>
        <Button
          size="lg"
          onClick={submit}
          disabled={saving}
          className="bg-[#921A0E] hover:bg-[#7d1611] text-white gap-2 px-8"
          data-testid="ingreso-form-guardar"
        >
          <CheckCircle size={20} weight="bold" /> {saving ? "Guardando…" : "REGISTRAR INGRESO"}
        </Button>
      </div>
    </div>
  );
}

function IngresoDetalle({ ingreso, onBack, onReimprimir }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showRevert, setShowRevert] = useState(false);
  const [reverting, setReverting] = useState(false);
  const puedeCargarCompra = user?.is_admin || (user?.permissions || []).includes("compras");

  const revertir = async () => {
    setReverting(true);
    try {
      const { data } = await api.post(`/ingresos-mercaderia/${ingreso.id}/revertir`);
      const eliminados = (data.lotes_afectados || []).filter((l) => l.eliminado).length;
      const modificados = (data.lotes_afectados || []).length - eliminados;
      toast.success(`Ingreso revertido · ${eliminados} lote(s) eliminado(s), ${modificados} lote(s) descontado(s)`);
      setShowRevert(false);
      onBack();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo revertir");
    } finally { setReverting(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Button variant="outline" size="sm" onClick={onBack} className="gap-1">
          <ArrowLeft size={16} /> Volver
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold font-heading truncate">{ingreso.proveedor_nombre || "—"}</h1>
          <div className="text-xs text-neutral-500">
            {fmtDate(ingreso.fecha)} · Ingreso de Mercadería
            {ingreso.compra_numero && ` · Compra #${ingreso.compra_numero}`}
          </div>
        </div>
        <IngresoBadge estado={ingreso.estado} />
        <Button size="sm" variant="outline" onClick={onReimprimir} className="gap-1" data-testid="btn-reprint-ingreso">
          <Printer size={14} /> Reimprimir etiquetas
        </Button>
        {ingreso.estado === "pendiente_compra" && puedeCargarCompra && (
          <Button
            size="sm"
            onClick={() => navigate(`/compras?ingreso_id=${ingreso.id}`)}
            className="gap-1 bg-neutral-900 hover:bg-neutral-800 text-white"
            data-testid="btn-detalle-cargar-compra"
          >
            <Receipt size={14} weight="bold" /> Cargar compra
          </Button>
        )}
        {ingreso.estado !== "revertida" && ingreso.estado !== "con_compra" && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowRevert(true)}
            className="gap-1 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
            data-testid="btn-revert-ingreso"
          >
            <ArrowCounterClockwise size={14} /> Revertir
          </Button>
        )}
      </div>

      <div className="border border-neutral-300 rounded-lg bg-white overflow-hidden mb-4">
        <div className="px-4 py-2 bg-neutral-50 border-b border-neutral-200 grid grid-cols-12 gap-3 text-xs font-mono uppercase text-neutral-600">
          <div className="col-span-6">Producto</div>
          <div className="col-span-2 text-right">Peso real</div>
          <div className="col-span-1 text-center">Bultos</div>
          <div className="col-span-3">Condición</div>
        </div>
        {(ingreso.items || []).map((it, idx) => (
          <div key={idx} className="border-t border-neutral-200 px-4 py-3 grid grid-cols-12 gap-3 items-center text-sm">
            <div className="col-span-6 min-w-0">
              <div className="font-semibold truncate">{it.descripcion}</div>
              {it.codigo && <div className="text-xs text-neutral-500 font-mono">Cód: {it.codigo}</div>}
              {it.observaciones && <div className="text-xs text-amber-700 mt-1">{it.observaciones}</div>}
            </div>
            <div className="col-span-2 text-right font-mono font-semibold">{(it.peso_real || 0).toLocaleString("es-AR")} kg</div>
            <div className="col-span-1 text-center font-mono">{it.cantidad_bultos ?? "—"}</div>
            <div className="col-span-3">
              <Badge className={`${CONDICION_STYLES[it.condicion]} border-0`}>{(it.condicion || "ok").toUpperCase()}</Badge>
            </div>
          </div>
        ))}
      </div>

      {ingreso.observaciones && (
        <div className="border border-neutral-200 rounded-lg p-3 bg-neutral-50 mb-4 text-sm">
          <div className="text-xs uppercase font-mono text-neutral-500 mb-1">Observaciones</div>
          <div>{ingreso.observaciones}</div>
        </div>
      )}

      <AlertDialog open={showRevert} onOpenChange={(v) => !reverting && setShowRevert(v)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revertir ingreso</AlertDialogTitle>
            <AlertDialogDescription>
              Se descontarán los kilos aportados al lote de cada producto. Sólo funciona si el lote todavía tiene esos kilos disponibles.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reverting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={revertir}
              disabled={reverting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="btn-confirm-revert-ingreso"
            >
              {reverting ? "Revirtiendo…" : "Revertir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function LotesGeneradosDialog({ open, lotes, proveedor, onClose }) {
  const printer = useZebraPrinter();
  const [copiesByLote, setCopiesByLote] = useState({});
  const setCopies = (id, n) => setCopiesByLote((s) => ({ ...s, [id]: Math.max(1, parseInt(n) || 1) }));
  const getCopies = (id) => copiesByLote[id] || 1;
  const zebraConfigRef = useRef(null);

  useEffect(() => {
    api.get("/label-templates/zebra_traceability")
      .then(({ data }) => { zebraConfigRef.current = data?.config || null; })
      .catch(() => { zebraConfigRef.current = null; });
  }, []);

  const imprimir = async (l) => {
    const baseUrl = window.location.origin;
    const qrUrl = `${baseUrl}/trazabilidad/${l.id}`;
    const totalBultos = (l.origenes || []).reduce((acc, o) => acc + (Number(o.cantidad_bultos) || 0), 0);
    const zpl = generarZplEtiqueta({
      nombre: l.descripcion,
      variante: proveedor ? `Prov: ${proveedor}` : "",
      peso: l.kilos_entrada,
      bultos: totalBultos > 0 ? totalBultos : null,
      fechaElab: l.fecha_elaboracion,
      fechaVenc: l.fecha_vencimiento,
      lote: l.numero,
      qrUrl,
      frigorifico: "LA BIFERIA S.R.L.",
      cuitFrigorifico: "",
      copies: getCopies(l.id),
      config: zebraConfigRef.current,
    });
    await printer.print(zpl);
  };

  const imprimirTodos = async () => {
    for (const l of lotes) { await imprimir(l); await new Promise((r) => setTimeout(r, 250)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Barcode size={24} weight="duotone" className="text-[#921A0E]" /> Lotes generados
          </DialogTitle>
          <DialogDescription>
            Se generaron {lotes.length} lote{lotes.length === 1 ? "" : "s"}. Podés imprimirlas ahora o más tarde desde el módulo Lotes.
          </DialogDescription>
        </DialogHeader>

        <div className="mb-3 border border-neutral-200 rounded-lg bg-neutral-50 p-3 flex items-center justify-between">
          <div className="text-sm">
            <span className="font-mono uppercase text-neutral-500 text-xs">Impresora Zebra: </span>
            {printer.device ? (
              <span className="font-medium text-emerald-700">✓ {printer.device.productName || "Conectada"}</span>
            ) : (
              <span className="text-neutral-500">no conectada</span>
            )}
          </div>
          {!printer.device && (
            <Button size="sm" onClick={printer.connect} disabled={printer.connecting || !printer.supported} className="gap-1">
              <Printer size={14} /> {printer.connecting ? "Conectando…" : "Conectar Zebra"}
            </Button>
          )}
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {lotes.map((l) => (
            <div key={l.id} className="border border-neutral-300 rounded-lg p-3 flex items-center gap-3 bg-white">
              <Badge className="bg-[#921A0E] text-white border-0 font-mono px-3 py-1 text-base">{l.numero}</Badge>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{l.descripcion}</div>
                <div className="text-xs text-neutral-500">
                  Elab {fmtDate(l.fecha_elaboracion)} · Venc {fmtDate(l.fecha_vencimiento)} · {l.kilos_entrada} kg
                </div>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-neutral-500">Copias:</span>
                <NumberInput min={1} max={99} value={getCopies(l.id)} onChange={(e) => setCopies(l.id, e.target.value)} className="w-14 h-9 text-center" />
              </div>
              <Button size="sm" onClick={() => imprimir(l)} disabled={!printer.device} className="gap-1 bg-neutral-800 hover:bg-neutral-900 text-white">
                <Printer size={14} /> Imprimir
              </Button>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center pt-3 border-t border-neutral-200">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          {printer.device && lotes.length > 0 && (
            <Button onClick={imprimirTodos} className="gap-2 bg-[#921A0E] hover:bg-[#7d1611] text-white">
              <Printer size={16} weight="fill" /> Imprimir todas ({lotes.length})
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IngresoBadge({ estado }) {
  const map = {
    pendiente_compra: { label: "Pendiente de compra", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    con_compra: { label: "Con compra", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    revertida: { label: "Revertida", cls: "bg-neutral-100 text-neutral-700 border-neutral-300 line-through" },
  };
  const s = map[estado] || map.pendiente_compra;
  return <Badge className={`${s.cls} border font-medium`}>{s.label}</Badge>;
}

function StatCard({ label, value, icon: Icon, color = "text-neutral-800" }) {
  return (
    <div className="border border-neutral-300 rounded-lg p-3 bg-white">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} weight="duotone" className={color} />
        <div className="text-xs uppercase font-mono text-neutral-500">{label}</div>
      </div>
      <div className={`text-xl font-bold ${color}`}>{value}</div>
    </div>
  );
}
